import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  ChefHat, 
  BarChart3, 
  Package, 
  Users, 
  Globe, 
  Sparkles,
  ArrowRight,
  Check,
  Star,
  Menu,
  X,
  Play,
} from "lucide-react";
import { useState, useEffect } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function useDemoEnabled() {
  const [enabled, setEnabled] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/api/site-settings`).then(r => r.json())
      .then(d => setEnabled(d.demo_enabled !== false))
      .catch(() => setEnabled(true));
  }, []);
  return enabled;
}

const features = [
  {
    icon: ChefHat,
    title: "Gestión de Menús con IA",
    description: "Crea menús profesionales en minutos. La IA genera platos, descripciones y precios sugeridos automáticamente."
  },
  {
    icon: BarChart3,
    title: "Dashboard Inteligente",
    description: "Visualiza ventas, márgenes y KPIs en tiempo real. Toma decisiones basadas en datos."
  },
  {
    icon: Package,
    title: "Control de Inventario",
    description: "Gestiona stock, proveedores y pedidos. Alertas automáticas de reposición."
  },
  {
    icon: Users,
    title: "Fidelización de Clientes",
    description: "Sistema de puntos y recompensas para aumentar la recurrencia de tus clientes."
  },
  {
    icon: Globe,
    title: "Web del Restaurante",
    description: "Genera una página web moderna con tu menú actualizado automáticamente."
  },
  {
    icon: Sparkles,
    title: "Previsiones con IA",
    description: "Predice ventas y necesidades de compra. Optimiza tu stock y reduce desperdicio."
  }
];

const PLAN_DESCRIPTIONS = {
  plan_trial: "Descubre MenuGenius gratis",
  plan_starter: "Web + Fidelización para empezar",
  plan_essentials: "Todo lo que necesita un restaurante",
  plan_pro: "Para negocios en crecimiento",
  plan_premium: "Para cadenas y grupos",
};

const PLAN_CTA = {
  plan_trial: "Empezar gratis",
  plan_starter: "Elegir Starter",
  plan_essentials: "Elegir Essentials",
  plan_pro: "Probar 14 días gratis",
  plan_premium: "Elegir Premium",
};

const testimonials = [
  {
    name: "María García",
    role: "Propietaria, La Tasca del Puerto",
    content: "MenuGenius ha transformado cómo gestionamos nuestro restaurante. Ahorramos 10 horas semanales en tareas administrativas.",
    avatar: "MG"
  },
  {
    name: "Carlos Rodríguez",
    role: "Chef Ejecutivo, Grupo Gastro",
    content: "La función de previsión con IA nos ha ayudado a reducir el desperdicio en un 30%. Increíble herramienta.",
    avatar: "CR"
  },
  {
    name: "Ana Martínez",
    role: "Gerente, Café Central",
    content: "Montamos nuestra web y sistema de fidelización en una tarde. Nuestros clientes están encantados.",
    avatar: "AM"
  }
];

const faqs = [
  {
    question: "¿Necesito conocimientos técnicos?",
    answer: "No. MenuGenius está diseñado para propietarios y encargados sin experiencia técnica. La interfaz es intuitiva y la IA te guía en cada paso."
  },
  {
    question: "¿Puedo importar mi menú actual?",
    answer: "Sí. Puedes subir una foto o PDF de tu menú actual y nuestra IA lo digitalizará automáticamente."
  },
  {
    question: "¿Se integra con mi TPV?",
    answer: "Sí, en los planes Profesional y Premium. Soportamos los principales sistemas TPV del mercado."
  },
  {
    question: "¿Puedo cancelar en cualquier momento?",
    answer: "Absolutamente. No hay permanencia mínima. Puedes cancelar tu suscripción cuando quieras."
  }
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [betaMode, setBetaMode] = useState(false);
  const [betaMessage, setBetaMessage] = useState(
    "Estamos en Beta privada. Contáctanos para acceso anticipado."
  );
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const demoEnabled = useDemoEnabled();

  useEffect(() => {
    // Fetch landing-config + plans in parallel; landing-config decides whether
    // to render the plans grid or the soft Beta message.
    Promise.all([
      fetch(`${API_URL}/api/subscription/landing-config`)
        .then(r => r.ok ? r.json() : null)
        .catch(() => null),
      fetch(`${API_URL}/api/subscription/plans`)
        .then(r => r.json())
        .catch(() => []),
    ])
      .then(([cfg, data]) => {
        if (cfg) {
          setBetaMode(cfg.plans_visible === false);
          if (cfg.beta_message) setBetaMessage(cfg.beta_message);
        }
        const mapped = (Array.isArray(data) ? data : [])
          .filter(p => !p.is_trial)
          .map(p => ({
            id: p.plan_id,
            name: p.name,
            price: p.price,
            price_annual_final: p.price_annual_final,
            annual_savings: p.annual_savings,
            annual_discount_label: p.annual_discount_label,
            annual_monthly_equivalent: p.annual_monthly_equivalent,
            offer: p.offer,
            offer_price_monthly: p.offer_price_monthly,
            offer_price_annual: p.offer_price_annual,
            max_restaurants: p.max_restaurants || 1,
            period: "mes",
            description: PLAN_DESCRIPTIONS[p.plan_id] || p.name,
            features: p.features || [],
            cta: PLAN_CTA[p.plan_id] || "Empezar",
            popular: p.plan_id === "plan_essentials",
            credits: p.credits_monthly,
          }));
        setPlans(mapped);
      })
      .finally(() => setPlansLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Beta Banner */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-white text-center py-2 px-4">
        <p className="text-xs sm:text-sm font-medium">
          <span className="inline-flex items-center gap-1.5">
            <span className="bg-white/20 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">Beta</span>
            MenuGenius esta en version beta cerrada. El acceso es solo por invitacion.
          </span>
        </p>
      </div>

      {/* Navigation */}
      <nav className="fixed top-[36px] left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <ChefHat className="w-6 h-6 text-primary-foreground" />
              </div>
              <span className="font-heading text-xl font-semibold">MenuGenius</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">Funcionalidades</a>
              <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">Precios</a>
              <a href="#testimonials" className="text-muted-foreground hover:text-foreground transition-colors">Testimonios</a>
              <a href="#faq" className="text-muted-foreground hover:text-foreground transition-colors">FAQ</a>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="nav-login-btn">Iniciar sesión</Button>
              </Link>
              <Link to="/register">
                <Button className="rounded-full px-6" data-testid="nav-register-btn">
                  Empezar gratis
                </Button>
              </Link>
            </div>

            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background border-t border-border">
            <div className="px-4 py-4 space-y-4">
              <a href="#features" className="block text-muted-foreground hover:text-foreground">Funcionalidades</a>
              <a href="#pricing" className="block text-muted-foreground hover:text-foreground">Precios</a>
              <a href="#testimonials" className="block text-muted-foreground hover:text-foreground">Testimonios</a>
              <a href="#faq" className="block text-muted-foreground hover:text-foreground">FAQ</a>
              <div className="pt-4 space-y-2">
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full">Iniciar sesión</Button>
                </Link>
                <Link to="/register" className="block">
                  <Button className="w-full">Empezar gratis</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-40 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 gradient-warm" />
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                Potenciado con Inteligencia Artificial
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Gestiona tu restaurante como un{" "}
                <span className="text-primary">profesional</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-lg">
                La plataforma todo-en-uno para bares y restaurantes. Menús, recetas, inventario, fidelización y página web. Todo con IA.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="rounded-full px-8 text-lg h-14 w-full sm:w-auto" data-testid="hero-cta-btn">
                    Empezar gratis
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                {demoEnabled && (
                <Link to="/demo">
                  <Button size="lg" variant="outline" className="rounded-full px-8 text-lg h-14 w-full sm:w-auto relative overflow-hidden group border-primary/60 hover:border-primary" data-testid="hero-demo-btn">
                    <span className="absolute inset-0 bg-primary/5 group-hover:bg-primary/10 transition-colors" />
                    <Play className="w-5 h-5 mr-2 text-primary" />
                    Probar demo interactiva
                  </Button>
                </Link>
                )}
              </div>

              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-primary" />
                  Sin tarjeta de crédito
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-primary" />
                  14 días gratis
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1753019491860-128b7763f8ee?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                  alt="Interior de restaurante moderno gestionado con MenuGenius"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
              
              {/* Floating cards */}
              <Card className="absolute -bottom-6 -left-6 shadow-lg animate-slide-up" style={{ animationDelay: "0.2s" }}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">+32%</p>
                    <p className="text-sm text-muted-foreground">Ventas este mes</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="absolute -top-4 -right-4 shadow-lg animate-slide-up" style={{ animationDelay: "0.4s" }}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">2,847</p>
                    <p className="text-sm text-muted-foreground">Clientes activos</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Todo lo que necesitas para tu negocio
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Una suite completa de herramientas diseñadas específicamente para la hostelería
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={`feat-${feature.title}`} 
                className="bg-card border-none shadow-soft hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                    <feature.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* ── Accessibility-as-a-differentiator banner ─────────────────
              B2B hospitality SaaS rarely talks about a11y. We do, and we
              make it a selling point: kitchens have ageing crews, busy
              hands, dim lighting, and frequent screen-reader users in the
              back office. Aligned with the EU Accessibility Act
              (June 2025). */}
          <div
            className="mt-12 rounded-3xl bg-gradient-to-br from-primary/5 via-card to-card border border-primary/15 p-8 lg:p-10"
            data-testid="landing-a11y-banner"
          >
            <div className="grid lg:grid-cols-[auto_1fr_auto] items-center gap-6 lg:gap-8">
              {/* Universal access icon — the same glyph used in the footer seal */}
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                <svg viewBox="0 0 24 24" className="w-8 h-8 text-primary" fill="currentColor" aria-hidden="true">
                  <circle cx="12" cy="4" r="2" />
                  <path d="M19 7h-5v15h-2v-7h-2v7H8V7H3v2h4v13h2v-7h6v7h2V9h4z" />
                </svg>
              </div>

              <div>
                <h3 className="font-heading text-xl sm:text-2xl font-semibold mb-2">
                  Diseñado para todos los que trabajan en tu local
                </h3>
                <p className="text-muted-foreground leading-relaxed max-w-2xl">
                  Tu equipo —camareros con prisa, cocineros con manos llenas, contables que usan lector
                  de pantalla, propietarios con vista cansada— puede usar MenuGenius sin barreras.
                  Auditamos cada despliegue con axe-core y nos alineamos con WCAG 2.1 AA y la
                  <a
                    href="https://eur-lex.europa.eu/eli/dir/2019/882/oj"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline ml-1"
                  >
                    Ley Europea de Accesibilidad (junio 2025)
                  </a>.
                </p>
              </div>

              <div className="flex flex-wrap gap-2 lg:flex-col lg:gap-2 lg:items-end">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-card border border-border text-xs font-medium whitespace-nowrap">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Daltonismo seguro
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-card border border-border text-xs font-medium whitespace-nowrap">
                  <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
                  Lector de pantalla
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-card border border-border text-xs font-medium whitespace-nowrap">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                  Navegación con teclado
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-card border border-border text-xs font-medium whitespace-nowrap">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  Zoom hasta 500%
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              {betaMode ? "Acceso anticipado" : "Planes adaptados a tu negocio"}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {betaMode ? betaMessage : "Empieza gratis y escala según crezcas. Sin permanencia."}
            </p>
          </div>

          {/* Monthly / Annual toggle — only shown when plans are visible */}
          {!betaMode && (
          <div className="flex items-center justify-center gap-2 mb-12" data-testid="landing-billing-toggle">
            <button
              type="button"
              onClick={() => setBillingPeriod("monthly")}
              data-testid="landing-toggle-monthly"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                billingPeriod === "monthly"
                  ? "bg-primary text-primary-foreground shadow"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              Mensual
            </button>
            <button
              type="button"
              onClick={() => setBillingPeriod("annual")}
              data-testid="landing-toggle-annual"
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                billingPeriod === "annual"
                  ? "bg-primary text-primary-foreground shadow"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              Anual
              {plans.some(p => p.annual_savings > 0) && (
                <span className="ml-2 px-1.5 py-0.5 bg-emerald-500/20 text-emerald-500 text-[10px] rounded-full font-bold">AHORRA</span>
              )}
            </button>
          </div>
          )}

          {betaMode ? (
            <div
              className="max-w-2xl mx-auto text-center bg-muted/40 border border-border rounded-2xl p-10"
              data-testid="landing-beta-private-block"
            >
              <p className="text-muted-foreground text-base">
                Si ya tienes una invitación, accede desde el botón <span className="font-medium text-foreground">"Iniciar sesión"</span> arriba a la derecha.
              </p>
            </div>
          ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {plansLoading ? (
              [0, 1, 2, 3].map(i => (
                <Card key={i} className="border border-border shadow-soft animate-pulse">
                  <CardContent className="p-6 space-y-4">
                    <div className="h-6 bg-muted rounded w-1/2" />
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-10 bg-muted rounded w-1/3 mt-4" />
                    <div className="space-y-3 mt-6">
                      {[0, 1, 2, 3].map(j => (
                        <div key={j} className="h-4 bg-muted rounded w-full" />
                      ))}
                    </div>
                    <div className="h-10 bg-muted rounded w-full mt-6" />
                  </CardContent>
                </Card>
              ))
            ) : plans.length > 0 ? (
              plans.map((plan) => (
              <Card 
                key={plan.id} 
                className={`relative overflow-hidden transition-all duration-300 hover:-translate-y-2 ${
                  plan.popular 
                    ? "border-2 border-primary shadow-lg scale-105" 
                    : "border border-border shadow-soft"
                }`}
                data-testid={`pricing-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-4 py-1 text-sm font-medium rounded-bl-xl">
                    Más popular
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="font-heading text-2xl font-semibold mb-2">{plan.name}</h3>
                  <p className="text-muted-foreground mb-6">{plan.description}</p>
                  
                  <div className="mb-2">
                    {(() => {
                      const isAnnual = billingPeriod === "annual";
                      const hasAnnualOffer = !!plan.offer && plan.offer_price_annual;
                      const hasMonthlyOffer = !!plan.offer && plan.offer_price_monthly;
                      if (isAnnual) {
                        const finalAnnual = hasAnnualOffer ? plan.offer_price_annual : plan.price_annual_final;
                        const originalAnnual = plan.price_annual_final || plan.price * 12;
                        const monthlyEq = finalAnnual ? (finalAnnual / 12).toFixed(2) : null;
                        return (
                          <>
                            <span className="text-4xl font-bold">{finalAnnual}€</span>
                            <span className="text-muted-foreground">/año + IVA</span>
                            {hasAnnualOffer && originalAnnual > finalAnnual && (
                              <span className="text-sm text-muted-foreground line-through ml-2">{originalAnnual}€</span>
                            )}
                            {monthlyEq && (
                              <p className="text-xs text-primary font-medium mt-1">
                                Equivale a {monthlyEq}€/mes
                              </p>
                            )}
                          </>
                        );
                      }
                      return hasMonthlyOffer ? (
                        <>
                          <span className="text-4xl font-bold">{plan.offer_price_monthly}€</span>
                          <span className="text-muted-foreground">/mes + IVA</span>
                          <span className="text-sm text-muted-foreground line-through ml-2">{plan.price}€</span>
                        </>
                      ) : (
                        <>
                          <span className="text-4xl font-bold">{plan.price}€</span>
                          <span className="text-muted-foreground">/mes + IVA</span>
                        </>
                      );
                    })()}
                  </div>
                  {plan.max_restaurants > 1 && (
                    <p className="text-xs text-muted-foreground mb-1">Hasta {plan.max_restaurants} restaurantes</p>
                  )}
                  {billingPeriod === "monthly" && plan.annual_savings > 0 && (
                    <p className="text-xs text-muted-foreground mb-1">
                      Al pagar anual: {plan.annual_monthly_equivalent}€/mes ({plan.annual_discount_label})
                    </p>
                  )}
                  {plan.credits > 0 && (
                    <p className="text-sm text-primary font-medium mb-6" data-testid={`plan-credits-${plan.id}`}>
                      {billingPeriod === "annual" ? `${plan.credits * 12} créditos IA / año` : `${plan.credits} créditos IA / mes`}
                    </p>
                  )}

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to={plan.id === "plan_starter" ? "/precio-starter" : "/register"}>
                    <Button 
                      className={`w-full rounded-full ${plan.popular ? "" : "bg-secondary hover:bg-secondary/90"}`}
                      variant={plan.popular ? "default" : "secondary"}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))
            ) : (
              <div className="md:col-span-2 lg:col-span-4 text-center py-12 text-muted-foreground">
                <p>Los planes estarán disponibles próximamente.</p>
              </div>
            )}
          </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Lo que dicen nuestros clientes
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Más de 500 restaurantes ya confían en MenuGenius
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={`test-${testimonial.name}`} className="bg-card border-none shadow-soft">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-6 italic">"{testimonial.content}"</p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center font-semibold text-primary">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="font-semibold">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Preguntas frecuentes
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card 
                key={`faq-${faq.question.slice(0, 20)}`} 
                className="border border-border cursor-pointer transition-all duration-200"
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                data-testid={`faq-item-${index}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold pr-8">{faq.question}</h3>
                    <ArrowRight className={`w-5 h-5 text-muted-foreground transition-transform ${openFaq === index ? "rotate-90" : ""}`} />
                  </div>
                  {openFaq === index && (
                    <p className="mt-4 text-muted-foreground animate-fade-in">{faq.answer}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">
            ¿Listo para transformar tu negocio?
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            Únete a cientos de restaurantes que ya gestionan su negocio de forma inteligente
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              variant="secondary" 
              className="rounded-full px-10 text-lg h-14 bg-white text-primary hover:bg-white/90"
              data-testid="cta-final-btn"
            >
              Empezar ahora gratis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Demo CTA Section */}
      {demoEnabled && (
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent" />
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
            <Play className="w-4 h-4" /> Demo interactiva
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Descubre MenuGenius en accion
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Explora todas las funcionalidades de nuestra plataforma con datos reales de un restaurante demo. Sin registro, sin compromiso.
          </p>
          <Link to="/demo">
            <Button size="lg" className="rounded-full px-10 text-lg h-14 gap-3 shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-shadow" data-testid="demo-section-cta">
              <Play className="w-5 h-5" />
              Probar demo interactiva
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground mt-4">3-5 minutos de exploracion. Tour guiado disponible.</p>
        </div>
      </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <ChefHat className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-heading font-semibold">MenuGenius</span>
            </div>

            <div className="flex items-center gap-8 text-sm text-muted-foreground">
              <a href="#" className="hover:text-foreground transition-colors">Términos</a>
              <a href="#" className="hover:text-foreground transition-colors">Privacidad</a>
              <a href="#" className="hover:text-foreground transition-colors">Contacto</a>
              <a
                href="/accessibility"
                className="hover:text-foreground transition-colors inline-flex items-center gap-1.5"
                data-testid="landing-a11y-link"
                aria-label="Declaración de accesibilidad — alineado con WCAG 2.1 AA"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="w-3.5 h-3.5"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  {/* Universal access glyph (silhouette with outstretched arms) */}
                  <circle cx="12" cy="4" r="2" />
                  <path d="M19 7h-5v15h-2v-7h-2v7H8V7H3v2h4v13h2v-7h6v7h2V9h4z" />
                </svg>
                Accesibilidad
              </a>
            </div>

            <p className="text-sm text-muted-foreground">
              © 2024 MenuGenius. Todos los derechos reservados.
            </p>
          </div>

          {/* WCAG seal — appears below the main footer row so it doesn't
              crowd the legal links but is still discoverable. Aligned with
              the EU Accessibility Act (June 2025). */}
          <div className="mt-8 pt-6 border-t border-border/50 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden="true">
                <circle cx="12" cy="4" r="2" />
                <path d="M19 7h-5v15h-2v-7h-2v7H8V7H3v2h4v13h2v-7h6v7h2V9h4z" />
              </svg>
              <span>Alineado con WCAG 2.1 AA</span>
            </div>
            <p className="text-[11px] text-muted-foreground max-w-md">
              Auditamos cada despliegue con axe-core. Tu opinión nos ayuda a mejorar:{" "}
              <a href="mailto:accesibilidad@menugenius.online" className="underline hover:text-foreground">
                accesibilidad@menugenius.online
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Emergent Badge */}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://app.emergent.sh/?utm_source=emergent-badge"
        style={{
          display: "inline-flex",
          boxSizing: "border-box",
          width: 178,
          height: 40,
          padding: "8px 12px",
          alignItems: "center",
          gap: 8,
          borderRadius: 50,
          background: "#000",
          position: "fixed",
          bottom: 20,
          right: 20,
          textDecoration: "none",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontSize: 12,
          zIndex: 9999,
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M15.5702 8.13142C15.7729 8.0412 16.0007 8.18878 15.9892 8.4103C15.8374 11.3192 14.0965 14.0405 11.2531 15.3065C8.40964 16.5725 5.2224 16.0453 2.95912 14.2117C2.78676 14.072 2.82955 13.804 3.03219 13.7137L4.95677 12.8568C5.04866 12.8159 5.15446 12.823 5.24204 12.8725C6.73377 13.7153 8.59176 13.8649 10.2772 13.1145C11.9626 12.3641 13.0947 10.8833 13.4665 9.21075C13.4883 9.11256 13.5539 9.02918 13.6457 8.98827L15.5702 8.13142Z" fill="white"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M15.3066 4.74698L15.5067 5.19653C15.5759 5.35178 15.5061 5.53366 15.3508 5.60278L1.29992 11.8586C1.14467 11.9278 0.962794 11.8579 0.893675 11.7027L0.701732 11.2716L0.693457 11.2531C-1.10317 7.21778 0.711626 2.49007 4.74692 0.693443C8.78221 -1.10318 13.51 0.711693 15.3066 4.74698ZM2.82356 8.55367C2.63552 8.63739 2.41991 8.51617 2.40853 8.31065C2.28373 6.05724 3.53858 3.85787 5.72286 2.88536C7.90715 1.91286 10.3813 2.45199 11.9724 4.05256C12.1175 4.19854 12.0633 4.43988 11.8753 4.5236L2.82356 8.55367Z" fill="white"/>
        </svg>
        <p style={{ color: "#FFF", fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600, lineHeight: "20px", margin: 0, whiteSpace: "nowrap" }}>
          Made with Emergent
        </p>
      </a>
    </div>
  );
}
